import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Modal } from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { USER_URL } from 'helpers/url_helper';
import Select from 'react-select';
import { showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ManageUsers = () => {
    const { t } = useTranslation();
    const [isForm, setIsForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [rows, setRows] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });

    const fetchData = async () => {
        setLoading(true);
        const { success, body } = await get(`${USER_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}`);
        if (success) {
            setRows(body);
            setLoading(false);
        }
    };

    useEffect(fetchData, [pagination]);

    const handleToggle = () => {
        setIsForm(!isForm);
        setEditMode(false);
        setEditingId(null);
        setFormData({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const url = editMode ? `${USER_URL}/${editingId}` : USER_URL;
        const method = editMode ? put : post;

        const { success, body } = await method(url, formData);

        if (success) {
            showSuccessAlert(editMode ? t('User updated successfully!') : t('User created successfully!'));
            setIsForm(false);
            setFormData({});
            setEditMode(false);
            setEditingId(null);
            fetchData();
        }
    };

    const handleInvalidSubmit = () => {
        alert(t("Please enter all required fields correctly."));
    };

    const ROLE_OPTIONS = [
        { value: 5, label: t("Admin (Full Access)"), aliases: ["admin", "5", 5, "Admin"] },
        { value: 6, label: t("Doctor / Specialist"), aliases: ["doctor", "6", 6, "Doctor"] },
        { value: 7, label: t("Receptionist / Front Desk"), aliases: ["receptionist", "reception", "7", 7, "Receptionist"] },
        { value: 8, label: t("Patient Portal"), aliases: ["patient", "8", 8, "Patient"] },
        { value: 9, label: t("Accountant / Billing"), aliases: ["accountant", "billing", "9", 9, "Accountant"] }
    ];

    const getRoleSelectValue = (roleVal, roleName) => {
        if (!roleVal && !roleName) return null;
        const rStr = String(roleVal || '').trim().toLowerCase();
        const found = ROLE_OPTIONS.find(opt =>
            String(opt.value) === String(roleVal) ||
            opt.aliases?.includes(rStr) ||
            (roleName && opt.label.toLowerCase().includes(String(roleName).toLowerCase()))
        );
        if (found) return { value: found.value, label: found.label };
        if (roleName) return { value: roleVal, label: roleName };
        return { value: roleVal, label: `${t("Role")} ${roleVal}` };
    };

    const handleEdit = (row) => {
        const cleanName = (row.name || '').replace(/\s*\((Patient|Doctor|Admin|Receptionist|Staff|Team)\)/gi, '').trim();
        setFormData({
            ...row,
            name: cleanName || row.name,
            role: row.role ?? row.role_id
        });
        setEditingId(row.id || row.user_id);
        setEditMode(true);
        setIsForm(true);
    };

    const handleDelete = async (id, sts) => {
        const action = sts === 1 ? t('De-activate') : t('Activate');

        const { isConfirmed } = await showConfirmAlert(`${action} ${t('User')}`, `${t('Do you really want to')} ${action} ${t('this User')}?`,
            `${t('Yes')}, ${action} ${t('it')}!`, `${action}d!`, `${t('User has been updated successfully.')}`);

        if (isConfirmed) {
            const { success } = await del(`${USER_URL}/${id}?sts=${sts}`);
            if (success) {
                fetchData();
            }
        }
    };

    const getRoleBadge = (roleVal, roleName) => {
        let roleStr = '';
        const rNum = Number(roleVal);
        if (rNum === 5 || (typeof roleVal === 'string' && roleVal.toLowerCase().includes('admin'))) {
            roleStr = 'Admin';
        } else if (rNum === 6 || (typeof roleVal === 'string' && roleVal.toLowerCase().includes('doctor'))) {
            roleStr = 'Doctor';
        } else if (rNum === 7 || (typeof roleVal === 'string' && roleVal.toLowerCase().includes('reception'))) {
            roleStr = 'Receptionist';
        } else if (rNum === 8 || (typeof roleVal === 'string' && roleVal.toLowerCase().includes('patient'))) {
            roleStr = 'Patient';
        } else if (roleName) {
            roleStr = roleName;
        } else {
            roleStr = 'Staff';
        }

        switch (roleStr.toLowerCase()) {
            case 'admin':
            case 'superadmin':
                return (
                    <span className="badge px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', border: '1px solid #d8b4fe' }}>
                        👑 {roleStr}
                    </span>
                );
            case 'doctor':
                return (
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
                        🩺 {roleStr}
                    </span>
                );
            case 'receptionist':
            case 'reception':
                return (
                    <span className="badge bg-success-subtle text-success border border-success-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
                        📋 {roleStr}
                    </span>
                );
            case 'patient':
                return (
                    <span className="badge bg-info-subtle text-info border border-info-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
                        👤 {roleStr}
                    </span>
                );
            default:
                return (
                    <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold">
                        {roleStr}
                    </span>
                );
        }
    };

    const tableColumns = [
        {
            dataField: 'user_id',
            text: '#',
            style: { width: '45px', textAlign: 'center', verticalAlign: 'middle' },
            formatter: (cell, row, rowIndex) => (
                <span className="badge bg-light text-muted border font-size-11 font-weight-bold">
                    {(pagination?.page - 1) * (pagination?.limit || 10) + (rowIndex + 1)}
                </span>
            )
        },
        {
            dataField: 'name',
            text: 'Full Name',
            style: { verticalAlign: 'middle' },
            formatter: (cell, row) => {
                const name = row.name || cell || 'N/A';
                const cleanName = name.replace(/\s*\([^)]*\)/g, '').trim();
                const initials = cleanName.split(' ').filter(Boolean).map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'U';
                return (
                    <div className="d-flex align-items-center gap-2.5 py-1">
                        <div
                            className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center font-weight-bold font-size-12 flex-shrink-0 shadow-2xs"
                            style={{ width: '34px', height: '34px' }}
                        >
                            {initials}
                        </div>
                        <div>
                            <div className="font-weight-bold text-dark font-size-13">
                                {cleanName}
                            </div>
                            {row.username && (
                                <span className="text-muted font-size-11">@{row.username}</span>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            dataField: 'email',
            text: 'Email',
            style: { verticalAlign: 'middle' },
            formatter: (cell) => cell ? (
                <div className="d-flex align-items-center gap-1 font-size-12 text-dark">
                    <i className="bx bx-envelope text-muted font-size-14 flex-shrink-0" />
                    <a href={`mailto:${cell}`} className="text-reset hover-primary text-decoration-none">{cell}</a>
                </div>
            ) : <span className="text-muted font-size-12">—</span>
        },
        {
            dataField: 'mobile',
            text: 'Mobile',
            style: { verticalAlign: 'middle' },
            formatter: (cell) => cell ? (
                <div className="d-flex align-items-center gap-1 font-size-12 text-dark">
                    <i className="bx bx-phone text-success font-size-14 flex-shrink-0" />
                    <a href={`tel:${cell}`} className="text-reset hover-primary text-decoration-none font-weight-medium">{cell}</a>
                </div>
            ) : <span className="text-muted font-size-12">—</span>
        },
        {
            dataField: 'role',
            text: 'Role',
            style: { verticalAlign: 'middle' },
            formatter: (cell, row) => getRoleBadge(row.role || cell, row.user_role?.name)
        },
        {
            dataField: 'actions',
            text: 'Action',
            isDummyField: true,
            style: { verticalAlign: 'middle', textAlign: 'center', width: '110px' },
            headerStyle: { textAlign: 'center', width: '110px' },
            formatter: (cell, row) => (
                <div className="d-flex align-items-center justify-content-center gap-1.5">
                    <button
                        type="button"
                        className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 font-size-11 px-2.5 py-1 rounded-pill font-weight-semibold shadow-2xs"
                        onClick={() => handleEdit(row)}
                        title={t("Edit User")}
                    >
                        <i className="fas fa-pencil-alt font-size-10" /> {t("Edit")}
                    </button>
                </div>
            )
        }
    ];

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Datatables
                        isSearch={true}
                        columns={tableColumns}
                        showTableOnly={true}
                        rowsLength={rows?.totalItems || 0}
                        rows={rows?.items || []}
                        keyField={'id'}
                        handleAddButton={handleToggle}
                        title={t("All Staff & Team Members")}
                        isAdd={true}
                        isTableHead={true}
                        loading={loading}
                        ssr={setPagination}
                    />
                </CardBody>
            </Card>

            <Modal isOpen={isForm} toggle={handleToggle} size="xl" centered>
                <AvForm onValidSubmit={handleSubmit} onInvalidSubmit={handleInvalidSubmit} model={formData}>
                    <div className="modal-header">
                        <h5 className="modal-title font-weight-bold">
                            {editMode ? t('Update Staff / Team Member') : t('Add New Staff / Team Member')}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4">
                        <Row className="g-3">
                            <Col md={6}>
                                <div className="mb-2">
                                    <AvField
                                        name="name"
                                        label={t("Full Name *")}
                                        type="text"
                                        placeholder={t("e.g. Sarah Jenkins")}
                                        value={formData.name || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Full Name")}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-2">
                                    <AvField
                                        name="mobile"
                                        label={t("Contact Number *")}
                                        type="text"
                                        placeholder={t("e.g. +91 9876543210")}
                                        value={formData.mobile || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Contact Number")}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-2">
                                    <AvField
                                        name="email"
                                        label={t("Email Address *")}
                                        type="email"
                                        placeholder={t("e.g. sarah@clinic.com")}
                                        value={formData.email || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Email Address")}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Access Role *")}</label>
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        name="role"
                                        isClearable={true}
                                        options={ROLE_OPTIONS}
                                        value={getRoleSelectValue(formData.role, formData.role_name)}
                                        onChange={(selected) => setFormData((prev) => ({ ...prev, role: selected ? selected.value : "" }))}
                                        placeholder={t("Select Role")}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-danger" onClick={handleToggle}>
                            {t("Cancel")}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? t('Processing...') : (editMode ? t('Update Member') : t('Save Team Member'))}
                        </button>
                    </div>
                </AvForm>
            </Modal>
        </React.Fragment>
    );
};

export default ManageUsers;
